import { Outlet, useMatch, useNavigate, useParams } from 'react-router';
import { Routes } from '../../../core/routes';

import { FlexGroup, ViewLayout, Text, Link, FlexItem, IconType } from '@lithos';
import { useGetEnvironment } from '../../../api/compute';
import { useUserSettings } from '../../context/UserSettingsContext';
import { TECTON_DOCUMENTATION_LINKS } from '../application-links';

export const getComputeRoutes = (): Record<string, string> => {
  return {
    Overview: './overview',
    Environments: './environments',
    'Compute Identities': './compute-identities',
  };
};

const useComputeTabs = () => {
  const navigation = useNavigate();
  const { user, serviceAccount, group } = useParams();
  const { canShowComputeIdentity } = useUserSettings();

  const isMatchComputeEnvironments = !!useMatch({ path: Routes.computeEnvironments, end: false });
  const isMatchComputeIdentities = !!useMatch({ path: Routes.computeIdentities, end: false });

  const indexRoute = {
    pathName: 'Overview',
    path: `./overview`,
    paramsContext: user,
    isMatch: !!useMatch({
      path: `${Routes.computeOverview}`,
      end: false,
    }),
  };

  const environments = [
    {
      pathName: 'Environments',
      path: `./environments`,
      paramsContext: serviceAccount,
      isMatch: isMatchComputeEnvironments,
    },
  ];

  const computeIdentities = canShowComputeIdentity
    ? [
        {
          pathName: 'Compute Identities',
          path: `./compute-identities`,
          paramsContext: group,
          isMatch: isMatchComputeIdentities,
        },
      ]
    : [];

  const pages = [indexRoute, ...environments, ...computeIdentities];

  const selectedPage = pages.find((page) => page.isMatch) ?? indexRoute;

  return {
    root: { label: selectedPage?.pathName },
    items: pages.map((page) => ({ label: page.pathName, value: page.pathName })),
    selectedTab: `${selectedPage?.pathName}`,
    setSelectedTab: (tab: string) => {
      const routes = getComputeRoutes();
      navigation(routes[tab]);
    },
  };
};

const ComputeOutlet = () => {
  const BREAD_CRUMB_ROOT_PAGE = 'Compute';
  const { environmentId } = useParams();
  const indexRoute = {
    pathName: 'Overview',
    path: `./overview`,
    paramsContext: 'overview',
    isMatch: !!useMatch({
      path: `${Routes.computeOverview}`,
      end: false,
    }),
  };

  const pages = [
    indexRoute,
    {
      pathName: 'Environments',
      path: `./environments`,
      paramsContext: 'environments',
      isMatch: !!useMatch({ path: Routes.computeEnvironments, end: false }),
    },
    {
      pathName: 'Compute Identities',
      path: `./compute-identities`,
      paramsContext: '',
      isMatch: !!useMatch({ path: Routes.computeIdentities, end: false }),
    },
  ];

  const selectedPage = pages.find((page) => page.isMatch) ?? indexRoute;

  const isEnvironmentDetailsPage =
    !!useMatch({ path: Routes.computeEnvironmentDetails, end: false }) && !!environmentId;

  const isEnvironmentsSelected = !!useMatch({ path: Routes.computeEnvironments, end: false });
  const isComputeIdentitiesSelected = !!useMatch({ path: Routes.computeIdentities, end: false });

  const { data, isLoading } = useGetEnvironment(environmentId ?? '', isEnvironmentDetailsPage);

  const environmentDetail = isLoading ? [] : [{ label: data?.remote_environment?.name ?? '' }];

  const breadcrumbs = {
    root: {
      label: BREAD_CRUMB_ROOT_PAGE,
      url: Routes.dataPlatformConfiguration,
    },
    items: [
      {
        label: selectedPage.paramsContext ? (
          <Link url={`./${selectedPage?.path}`} name={selectedPage?.pathName} />
        ) : (
          selectedPage?.pathName
        ),
        url: selectedPage.paramsContext ? `./${selectedPage?.path}` : undefined,
      },
      ...environmentDetail,
    ],
  };
  const title = isComputeIdentitiesSelected
    ? 'Compute Identities'
    : isEnvironmentsSelected
    ? 'Environments'
    : 'Compute';

  const subtitle = isComputeIdentitiesSelected ? (
    <FlexGroup>
      <FlexItem shrink="1" grow="0">
        <Text>Compute Identities manage access to compute resources in Tecton.</Text>
      </FlexItem>
      <FlexItem>
        <Link url={TECTON_DOCUMENTATION_LINKS.COMPUTE_IDENTITIES} name="Learn More" />
      </FlexItem>
    </FlexGroup>
  ) : isEnvironmentsSelected ? (
    <FlexGroup>
      <FlexItem shrink="1" grow="0">
        <Text>Environments manage Python for RTFV execution.</Text>
      </FlexItem>
      <FlexItem>
        <Link url={TECTON_DOCUMENTATION_LINKS.COMPUTE_ENVIRONMENTS} name="Learn More" />
      </FlexItem>
    </FlexGroup>
  ) : (
    <></>
  );

  return (
    <>
      <ViewLayout
        header={{ breadcrumbs: breadcrumbs, title, subtitle: subtitle, tabs: useComputeTabs() }}
        content={<Outlet />}
      />
    </>
  );
};

export default ComputeOutlet;
